import { Duplicate } from '@gmm/sdk-voodoo/examProblems';
import { ExerciseSkill } from '@gmm/sdk-voodoo/shared';
import create from 'zustand';
import { devtools } from 'zustand/middleware';

export type DUPLICATION_WARNING =
  | 'DELETED'
  | 'OUTDATED_COPIED'
  | 'UNAVAILABLE_RELOADING';

const getDuplicateKey = (dup: Duplicate): string =>
  `${dup.assignmentId}-${dup.skillId}-${dup.sourcePositionWithinSkill}`;

interface Duplicated extends Duplicate {
  saved: boolean;
}

interface DuplicationState {
  // key 'skillId:positionWithinSkill' in open (left side) exam
  // value 'examId:problemType:positionWithinSkill' source of copy (right side)
  duplicatedFromSkillDiscovery: Duplicated[];

  // marked as 'unsaved'
  addDuplicatedFromSkillDiscovery: (duplicates: Duplicate[]) => void;

  hasBeenDuplicatedFromSkillDiscoveryByIds: (
    sourceExamId: string,
    skillId: string,
    sourcePositionWithinSkill: number,
  ) => boolean;
  hasBeenDuplicatedFromSkillDiscovery: (lep: Duplicate) => boolean;

  // User clicks CANCEL or SAVE on open work
  cancelOrSaveDuplicatedFromSkillDiscovery: (saved?: boolean) => void;

  removeDuplicatedFromSkillDiscoveryByIds: (
    skillId: string,
    positionWithinSkill: number,
  ) => void;
  removeDuplicatedFromSkillDiscovery: (removals: Duplicate[]) => void;

  clearDuplicatedRecords: () => void;

  duplicationWarning: DUPLICATION_WARNING | undefined;
  setDuplicationWarning: (
    duplicationWarning: DUPLICATION_WARNING | undefined,
  ) => void;

  // when user clicks 'ADD PROBLEM' and no exam is open,
  // we stash duplicate(s) here until 'route' is finished
  // opening the create exam view
  stashedDuplicateSource: ExerciseSkill | undefined;
  setStashedDuplicateSource: (exerciseSkill: ExerciseSkill) => void;
  getStashedDuplicateSource: () => ExerciseSkill | undefined;
  clearStashedDuplicateSource: () => void;
}

export const useDuplicationStore = create<DuplicationState>()(
  devtools(
    (set, get) => ({
      duplicatedFromSkillDiscovery: [],
      duplicationWarning: undefined,
      stashedDuplicateSource: undefined,

      addDuplicatedFromSkillDiscovery: (duplicates: Duplicate[]) => {
        set(state => {
          const dups = [...state.duplicatedFromSkillDiscovery];
          for (const duplicate of duplicates) {
            dups.push({ ...duplicate, saved: false });
          }
          return { duplicatedFromSkillDiscovery: dups };
        });
      },

      // On 'SAVE', mark all as saved
      // On 'CANCEL', remove all unsaved
      cancelOrSaveDuplicatedFromSkillDiscovery: (saved?: boolean) => {
        if (saved) {
          set(state => {
            const dups = state.duplicatedFromSkillDiscovery.map(item => {
              return { ...item, saved: true };
            });
            return { duplicatedFromSkillDiscovery: dups };
          });
        } else {
          set(state => {
            const dups = state.duplicatedFromSkillDiscovery.filter(
              item => item.saved,
            );
            return { duplicatedFromSkillDiscovery: dups };
          });
        }
      },

      hasBeenDuplicatedFromSkillDiscoveryByIds: (
        sourceExamId: string,
        sourceSkillId: string,
        sourcePositionWithinSkill: number,
      ) =>
        get().duplicatedFromSkillDiscovery.some(
          dup =>
            dup.assignmentId.toString() === sourceExamId &&
            dup.skillId.toString() === sourceSkillId &&
            dup.sourcePositionWithinSkill === sourcePositionWithinSkill,
        ),

      hasBeenDuplicatedFromSkillDiscovery: (lep: Duplicate) => {
        return get().hasBeenDuplicatedFromSkillDiscoveryByIds(
          lep.assignmentId.toString(),
          lep.skillId.toString(),
          lep.sourcePositionWithinSkill,
        );
      },

      // called on click of refresh icon for exam template locked problem
      removeDuplicatedFromSkillDiscoveryByIds: (
        skillId: string,
        positionWithinSkill: number,
      ) => {
        set(state => {
          const dups = state.duplicatedFromSkillDiscovery.filter(
            item =>
              !(
                item.skillId.toString() === skillId &&
                item.destinationPositionWithinSkill === positionWithinSkill
              ),
          );
          return { duplicatedFromSkillDiscovery: dups };
        });
      },

      removeDuplicatedFromSkillDiscovery: (removals: Duplicate[]) => {
        if (removals.length === 0) return;

        set(state => {
          const removalKeys = new Set(removals.map(getDuplicateKey));
          return {
            duplicatedFromSkillDiscovery:
              state.duplicatedFromSkillDiscovery.filter(
                item => !removalKeys.has(getDuplicateKey(item)),
              ),
          };
        });
      },

      clearDuplicatedRecords: () => {
        set(state => {
          const { duplicatedFromSkillDiscovery: copiedFromSkillDiscovery } =
            state;
          if (copiedFromSkillDiscovery.length > 0) {
            return { duplicatedFromSkillDiscovery: [] };
          }
          return state;
        });
      },

      setDuplicationWarning: (
        duplicationWarning: DUPLICATION_WARNING | undefined,
      ) => {
        set({ duplicationWarning });
      },

      setStashedDuplicateSource: (exerciseSkill: ExerciseSkill) => {
        set({ stashedDuplicateSource: exerciseSkill });
      },

      getStashedDuplicateSource: () => {
        return get().stashedDuplicateSource;
      },

      clearStashedDuplicateSource: () => {
        set({ stashedDuplicateSource: undefined });
      },
    }),
    { name: 'ExamTemplateProblemStore' },
  ),
);
